import React, { useCallback, useEffect, useMemo } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
import { BLOCKS } from '@contentful/rich-text-types';
import { useLocation } from '@reach/router';

import extractArrayData from '../../../utils/extractArrayData';
import { ContentType, Contentful, DeviceType } from '../../../types';
import RichText from '../../UI/RichText';
import BannerCard from '../BannerCard/BannerCard';
import ProductCard from '../ProductCard/ProductCard';
import { bpProductBannerCarousel, bpProductCarousel } from '../breakpoints';
import slugify from '../../../utils/slugify';
import { Section, Headline, Carousel } from '../styles';
import {
  carouselAnalyticsEvents,
  addCarouselBulletAnalyticEvents,
} from '../utils/carouselAnalyticsEvents';
import TextLink from '../../UI/TextLink/TextLink';
import PhoneCard from '../../PhoneCard/PhoneCard';
import { ContentfulForm, PageSectionFormField } from '../../../ContentfulTypes';

import { TopWrapper } from './styles';

interface Props {
  data: Array<Record<string, any> | undefined>;
  className?: string;
  section?: string;
  phoneLabelData?: ContentfulForm;
  loading?: boolean;
}

// Elements to render on rich text
const headlineOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Headline>{children}</Headline>,
    [BLOCKS.HEADING_1]: (_, children) => <Headline>{children}</Headline>,
    [BLOCKS.HEADING_2]: (_, children) => <Headline>{children}</Headline>,
  },
};

const ProductCarousel = ({ data, className, phoneLabelData, loading = false }: Props) => {
  const location = useLocation();
  const heading = useMemo(() => extractArrayData(
    data,
    Contentful.TypeName,
    ContentType.ContentfulTextSection,
  ), [data]);

  const products = useMemo(() => extractArrayData(
    data,
    Contentful.TypeName,
    ContentType.ContentfulCombinatorSection,
  ), [data]);
  
  const cta = useMemo(
    () => extractArrayData(data, Contentful.TypeName, ContentType.ContentfulCta),
    [data],
  );

  // Get sectionId and slugify for classNames
  const sectionId = products?.analyticsID;
  const sectionTarget = slugify(sectionId ?? 'Carousel');

  const addAnalyticEvents = useCallback(
    () => addCarouselBulletAnalyticEvents(sectionTarget, sectionId),
    [sectionId, sectionTarget],
  );
  
  useEffect(addAnalyticEvents, [addAnalyticEvents]);
  const items = useMemo(() => products?.combinatorSectionItems?.map((item) => {
    if (item.__typename === ContentType.ContentfulBanner) {
      return (
        <SwiperSlide key={item.id} className={`promo ${item.bannerType}`} style={{ background: 'initial' }}>
          <BannerCard
            data={item}
            className={`product-carousel ${sectionTarget}`}
            section={sectionId}
          />
        </SwiperSlide>
      );
    }
    if (
      item.__typename === ContentType.ContentfulAccessory
    ) {
      return (
        <SwiperSlide key={item.id} style={{ background: 'initial' }}>
          <ProductCard
            data={item}
            className={`product-carousel ${sectionTarget}`}
            section={sectionId}
            type="accessory"
          />
        </SwiperSlide>
      );
    }
    if (
      item.__typename === ContentType.ContentfulPhone
    ) {
      return (
        <SwiperSlide key={item.id} style={{ background: 'initial' }}>
          <PhoneCard
            key={item.urlSlug}
            phone={item}
            location={location}
            phoneLabels={phoneLabelData?.formFields as Array<PageSectionFormField>}
          />
        </SwiperSlide>
      );
    }
    if (!item.__typename) {
      const ItemComponent = item;
      return ItemComponent;
    }
    return null;
  }), [
    products?.combinatorSectionItems,
    sectionTarget,
    sectionId,
    location,
    phoneLabelData?.formFields,
  ]);

  // Hide desktop next slide arrow for Product Banners that become a carousel for mobile
  const bpCarousel = items?.length <= 4 ? bpProductBannerCarousel : bpProductCarousel;

  if (!data) return null;
  const loadingItems = Array.from({ length: 6 }, (_, index) => (
    <SwiperSlide key={`loading-item-${index + 1}`} style={{ borderRadius: "16px" }}>
      <PhoneCard
        phone={{ cmsName: `loading-item-${index + 1}`, deviceType: DeviceType.Phone }}
        loading
      />
    </SwiperSlide>
  ));

  return (
    <Section className={className}>
      <TopWrapper>
        {heading && (
          <RichText document={heading.heading} options={headlineOptions} />
        )}
        {cta && (
          <TextLink
            linkText={cta?.ctaLabel?.ctaLabel}
            linkColor={cta?.ctaColor}
            linkSize="small"
            url={cta?.ctaUrl}
            route={cta?.ctaTarget}
          />
        )}
      </TopWrapper>
      <Carousel className={sectionTarget}>
        <Swiper
          tabIndex={0}
          speed={1000}
          breakpoints={bpCarousel}
          navigation
          pagination={{
            clickable: true,
          }}
          a11y={{
            slideRole: 'group',
            enabled: true,
          }}
          allowTouchMove
          keyboard
          modules={[Navigation, Pagination, Keyboard, A11y]}
          className={`swiper ${sectionTarget}`}
          {...carouselAnalyticsEvents(sectionId)}
        >
          {(loading || !items) ? loadingItems : items}
        </Swiper>
      </Carousel>
    </Section>
  );
};

export default ProductCarousel;
